import { uniq } from 'lodash';
import { KycProcessStatus, KycRiskRating } from 'further-types/kyc';
import { amlProcessStatusLabels } from 'further-ui/labels';

export const furtherCased = (str?: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatTypeOfFailureString = (type: string): string => {
  return uniq(
    type
      .split(',')
      .map((part) => part.trim())
      .map((part) => part.replace(/^pep,/, '')) // Remove leading 'pep,' from each part
      .map((part) => part.replace(/-/g, ' ')) // Replace hyphens with spaces
      .map((part) =>
        part
          .split(' ')
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(' '),
      )
      .filter((part) => part !== 'Pep' && part !== 'PEP') // Remove standalone PEP entries
      .filter((part) => part === 'Adverse Media' || !part.includes('Adverse'))
      .map(furtherCased),
  )
    .join(', ')
    .replace(/Pep/g, 'PEP'); // Replace Pep with PEP
};

export const formatCountryNamesString = (
  countryNames: Array<string>,
): string => {
  const uniqueCountries = uniq(countryNames);
  if (uniqueCountries.length === 0) return '-';
  if (uniqueCountries.length === 1) return uniqueCountries[0];
  if (uniqueCountries.length === 2) return uniqueCountries.join(', ');
  if (uniqueCountries.length === 3)
    return `${uniqueCountries[0]}, ${uniqueCountries[1]}, +1 other`;
  return `${uniqueCountries[0]}, ${uniqueCountries[1]}, +${
    uniqueCountries.length - 2
  } others`;
};

export const formatAmlStatusLabel = (
  status: KycProcessStatus,
  reason: Array<string>,
  countries: Array<string>,
  riskRating: KycRiskRating,
): string => {
  if (status === KycProcessStatus.Failed) {
    // Check if it's a country residency flag based on reason/countries
    if (
      (countries.length > 0 || reason.length > 0) &&
      (riskRating === KycRiskRating.High ||
        riskRating === KycRiskRating.MediumOrHigh)
    ) {
      return 'Country residency flag';
    }
    return 'Consider';
  }

  return amlProcessStatusLabels[status] || status;
};

export const formatLabel = (label: string): string => {
  if (label === 'Locationurl') {
    return 'Location Url:';
  }
  return label + ':';
};

export const formatSnippet = (snippet?: string): string => {
  if (!snippet) return '';
  return snippet
    .replace(/\\u[\dA-F]{4}/gi, (match) => {
      return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    })
    .replace(/<\/?[^>]+(>|$)/g, '') // Remove HTML tags
    .replace(/\\n/g, ' ') // Replace newlines with spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

export const formatAmlTypes = (amlTypes: Array<string>): Array<string> => {
  return amlTypes
    .filter((type) => type.includes('pep'))
    .map((type) => {
      return type.replaceAll('-', ' ').toLowerCase().replace('pep', 'PEP');
    });
};

export const sanitizeAmlData = (
  data: Array<{ name?: string; value: string; snippet?: string }>,
): Array<{ name: string; value: string }> => {
  // First transform 'Original Country Text' to 'Country'
  const transformedData = data.map((item) => ({
    ...item,
    name: !item.name
      ? ''
      : item.name === 'Original Country Text'
      ? 'Country'
      : item.name.replace(/url/i, 'URL'),
  }));

  // Filter out items without name/value
  const filteredData = transformedData.filter(
    (item) => item.name && item.value,
  );

  // Handle duplicate URLs - prefer 'Related URL' over 'Location URL'
  const urls = filteredData.filter((item) =>
    item.name.toLowerCase().includes('url'),
  );
  const uniqueUrls = [...new Set(urls.map((item) => item.value))];
  const nonUrlData = filteredData.filter(
    (item) => !item.name.toLowerCase().includes('url'),
  );

  // Add back unique URLs
  uniqueUrls.forEach((url) => {
    nonUrlData.push({ name: 'Related URL', value: url });
  });

  // Group countries together
  const countries = nonUrlData
    .filter((item) => item.name === 'Country')
    .map((item) => item.value);

  // Remove individual country entries and add combined one if there are any
  const nonCountryData = nonUrlData.filter((item) => item.name !== 'Country');
  if (countries.length > 0) {
    const uniqueCountries = [...new Set(countries)];
    nonCountryData.push({
      name: uniqueCountries.length === 1 ? 'Country' : 'Countries',
      value: uniqueCountries.join(', '),
    });
  }

  // Convert to 'Further casing'
  // All names should start with a capital then lower case all subsequent words
  const furtherCasedData = nonCountryData.map((item) => ({
    ...item,
    name: furtherCased(item.name).replace('url', 'URL'),
  }));

  // Sort the data alphabetically by name
  return [...furtherCasedData].sort((a, b) => {
    // Always put Country/Countries first
    if (a.name === 'Country' || a.name === 'Countries') return -1;
    if (b.name === 'Country' || b.name === 'Countries') return 1;
    // Sort the rest alphabetically
    return a.name.localeCompare(b.name);
  });
};
